import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box } from 'ui/Box';
import { Header } from '../Header';
import { RegisterMainScreen } from '../RegisterMainScreen';
import { Steps } from 'components/Steps';
import { CommunicationTrait, Gender, RegisterData } from 'types/register';
import { Step1 } from 'modules/RegisterModule/components/RegisterSteps/Step1';
import { Step2 } from 'modules/RegisterModule/components/RegisterSteps/Step2';
import { Step3 } from 'modules/RegisterModule/components/RegisterSteps/Step3';
import { Step4 } from 'modules/RegisterModule/components/RegisterSteps/Step4';
import { Step5 } from 'modules/RegisterModule/components/RegisterSteps/Step5';
import { Step6 } from 'modules/RegisterModule/components/RegisterSteps/Step6';
import { Step7 } from 'modules/RegisterModule/components/RegisterSteps/Step7';
import { Step8 } from 'modules/RegisterModule/components/RegisterSteps/Step8';
import { Step9 } from 'modules/RegisterModule/components/RegisterSteps/Step9';
import { CompanyData, User, UserRole } from 'types/api.d';

import { useRegister } from 'modules/RegisterModule/hooks/useRegister';
import { useUserStore } from 'store';
import { useRegisterCompany } from 'modules/RegisterModule/hooks/useRegisterCompany';
import { useValidateInvite } from 'modules/RegisterModule/hooks/useValidateInvite';
import { useFetchProfile } from 'hooks/useFetchProfile';
import { appRoutes } from 'utils';
export interface RegisterProps {}
export const Register: FC<RegisterProps> = memo(() => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isStepsView, setIsStepsView] = useState(false);
  const [isAdditionalSteps, setAdditionalSteps] = useState(false);
  const [searchParameters] = useSearchParams();
  const navigate = useNavigate();

  const [registerData, setRegisterData] = useState<RegisterData>({
    employeeName: '',
    gender: 'male',
    communicationTrait: 'formal',
    name: '',
    inviteId: '',
  });
  const [companyData, setCompanyData] = useState<CompanyData>({
    name: '',
    website_link: '',
    industry: '',
    employees_num: '',
    mission: '',
  });

  const { status: registerStatus, error, register } = useRegister();
  const {
    errors: createCompanyErrors,
    status: companyRegisterStatus,
    hasCompany,
    registerCompany,
  } = useRegisterCompany();
  const { full_name, role, company, setUser } = useUserStore();
  const { status: fetchProfileStatus, data, fetchProfile } = useFetchProfile();
  const {
    validateInvite,
    isValid,
    status: validateStatus,
  } = useValidateInvite();

  const registerUser = async (password: string) => {
    await register(registerData, password);
  };

  const navigateToStepsView = () => setIsStepsView(true);
  const nextStep = useCallback(() => setCurrentStep((prev) => prev + 1), []);
  const prevStep = useCallback(() => {
    setCurrentStep((prevStep) => {
      if (prevStep === 0) {
        setIsStepsView(false);
        return 0;
      }
      return prevStep - 1;
    });
  }, []);

  const onSubmitStep1 = (employeeName: string) => {
    setRegisterData({ ...registerData, employeeName });
    nextStep();
  };

  const onSubmitStep3 = (gender: Gender) => {
    setRegisterData({ ...registerData, gender });
    nextStep();
  };

  const onSubmitStep4 = (communicationTrait: CommunicationTrait) => {
    setRegisterData({ ...registerData, communicationTrait });
    nextStep();
  };

  const onSubmitStep5 = (name: string) => {
    setRegisterData({ ...registerData, name });
    nextStep();
  };

  const steps = [
    <Step1
      onSubmit={onSubmitStep1}
      prevStep={prevStep}
      employeeName={registerData.employeeName}
    />,
    <Step2
      employeeName={registerData.employeeName}
      onSubmit={nextStep}
      prevStep={prevStep}
    />,
    <Step3
      onSubmit={onSubmitStep3}
      prevStep={prevStep}
      employeeName={registerData.employeeName}
      gender={registerData.gender}
    />,
    <Step4
      onSubmit={onSubmitStep4}
      prevStep={prevStep}
      employeeName={registerData.employeeName}
      communicationTrait={registerData.communicationTrait}
    />,
    <Step5
      onSubmit={onSubmitStep5}
      prevStep={prevStep}
      name={registerData.name}
    />,
    <Step6
      title={registerData.name}
      prevStep={prevStep}
      onSubmit={registerUser}
      error={error || ''}
      disabled={registerStatus === 'loading'}
    />,
  ];
  const additionalSteps = [
    <Step7
      companyData={companyData}
      name={full_name}
      onSubmit={(data) => {
        setCompanyData(data);
        nextStep();
      }}
      status={companyRegisterStatus}
      setCompanyData={setCompanyData}
      errors={createCompanyErrors}
    />,
    <Step7
      onSubmit={(data) => {
        registerCompany(data);
      }}
      status={companyRegisterStatus}
      prevStep={prevStep}
      companyData={companyData}
      confirm
    />,
    <Step8 nextStep={nextStep} />,
    <Step9 />,
  ];

  useEffect(() => {
    if (validateStatus === 'success') {
      if (!isValid) {
        navigate(appRoutes.login);
      }
    }
    if (validateStatus === 'failed') {
      navigate(appRoutes.login);
    }
  }, [validateStatus, isValid, navigate]);

  useEffect(() => {
    if (registerStatus === 'success') {
      navigate(appRoutes.login);
    }
  }, [registerStatus, navigate]);

  useEffect(() => {
    if (companyRegisterStatus === 'success') {
      nextStep();
      fetchProfile();
    }
    if (companyRegisterStatus === 'failed') {
      if (hasCompany) {
        fetchProfile();
      } else {
        prevStep();
      }
    }
  }, [companyRegisterStatus]);

  useEffect(() => {
    if (fetchProfileStatus === 'success') {
      setUser(data as User);
      if (hasCompany) {
        navigate(appRoutes.home);
      }
    }
  }, [fetchProfileStatus]);

  useEffect(() => {
    const access_token = localStorage.getItem('access_token');
    const inviteId = searchParameters.get('inviteId');
    if (access_token && role === UserRole.SA && !company) {
      if (!isAdditionalSteps) {
        setAdditionalSteps(true);
        navigateToStepsView();
      }
    } else if (inviteId) {
      validateInvite(inviteId);
      setRegisterData({ ...registerData, inviteId });
    } else {
      navigate(appRoutes.home);
    }
  }, []);

  return (
    <Box className="h-max-content flex min-h-screen w-full flex-1 justify-center p-4">
      <Box className="flex w-full flex-col rounded-3xl bg-light-gray px-4 py-5 md:px-20">
        <Header />
        <Box className="flex h-full flex-1 flex-col-reverse justify-center md:flex-col">
          {isStepsView && !isAdditionalSteps && (
            <Steps steps={steps.length} currentStep={currentStep} />
          )}
          {!isStepsView ? (
            <RegisterMainScreen navigateToStepsView={navigateToStepsView} />
          ) : (
            <>
              {isAdditionalSteps
                ? additionalSteps[currentStep]
                : steps[currentStep]}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
});
